import { gsap } from "gsap";

interface AnimationTarget {
  current: HTMLElement | null;
}

/**
 * Animates the mobile menu overlay opening with improved performance
 */
export const animateOverlayOpen = (
  overlayRef: AnimationTarget,
  callback: () => void
) => {
  if (!overlayRef.current) return;

  const tl = gsap.timeline({
    onComplete: callback,
  });

  tl.to(overlayRef.current, {
    duration: 0.4,
    opacity: 1,
    height: "100vh",
    ease: "power3.inOut",
  });
};

/**
 * Animates the mobile menu items entering with stagger
 */
export const animateMenuItemsEnter = () => {
  const menuItems = document.querySelectorAll(".menu-item");

  if (!menuItems.length) return;

  gsap.fromTo(
    menuItems,
    {
      opacity: 0,
      y: 30,
      scale: 0.9,
    },
    {
      opacity: 1,
      y: 0,
      scale: 1,
      duration: 0.5,
      stagger: 0.1,
      ease: "back.out(1.7)",
    }
  );
};

/**
 * Animates the mobile menu items exiting
 */
export const animateMenuItemsExit = (
  overlayRef: AnimationTarget,
  callback?: () => void
) => {
  if (!overlayRef.current) return;

  const menuItems = document.querySelectorAll(".menu-item");

  const tl = gsap.timeline({
    onComplete: callback,
  });

  // First animate menu items out
  if (menuItems.length) {
    tl.to(menuItems, {
      duration: 0.2,
      opacity: 0,
      y: -20,
      scale: 0.9,
      stagger: 0.03,
      ease: "power2.inOut",
    });
  }

  // Then collapse the overlay
  tl.to(overlayRef.current, {
    duration: 0.4,
    opacity: 0,
    height: 0,
    ease: "power3.inOut",
  });
};

/**
 * Animates the close button appearing with bounce effect
 */
export const animateCloseButtonEnter = (buttonRef: AnimationTarget) => {
  if (!buttonRef.current) return;

  gsap.fromTo(
    buttonRef.current,
    {
      opacity: 0,
      scale: 0.5,
      rotation: -180,
    },
    {
      opacity: 1,
      scale: 1,
      rotation: 0,
      duration: 0.4,
      delay: 0.2,
      ease: "back.out(1.7)",
    }
  );
};

/**
 * Animates the close button disappearing
 */
export const animateCloseButtonExit = (buttonRef: AnimationTarget) => {
  if (!buttonRef.current) return;

  gsap.to(buttonRef.current, {
    opacity: 0,
    scale: 0.5,
    rotation: 180,
    duration: 0.3,
    ease: "power2.in",
  });
};

/**
 * Adds hover animation to menu items
 */
export const animateMenuItemHover = (element: HTMLElement) => {
  if (!element) return;

  const handleMouseEnter = () => {
    gsap.to(element, {
      scale: 1.05,
      x: 10,
      duration: 0.3,
      ease: "power2.out",
    });
  };

  const handleMouseLeave = () => {
    gsap.to(element, {
      scale: 1,
      x: 0,
      duration: 0.3,
      ease: "power2.out",
    });
  };

  element.addEventListener("mouseenter", handleMouseEnter, { passive: true });
  element.addEventListener("mouseleave", handleMouseLeave, { passive: true });

  return () => {
    element.removeEventListener("mouseenter", handleMouseEnter);
    element.removeEventListener("mouseleave", handleMouseLeave);
  };
};
